import AddTestStepper from "../../components/add/AddTestStepper";
import AddTestPageNavButtons from "./AddTestPageNavButtons";
import VideoCard from "../global/VideoCard";
import DragDropFileUpload from "./DragDropFileUpload";
import React, { useContext, useState } from "react";
import { VideoContext } from "../../context/video_context";
import { X, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle_group";
import { cn } from "../../lib/utils";

type UploadTestContentProps = {
  handlePageChangeUp: any;
  handlePageChangeDown: any;
  handlePageChange: any;
};

const activeStep: number = 1;

export default function UploadTestContent(fn: UploadTestContentProps) {
  const videoContext = useContext(VideoContext) as any;
  const selectedVideo = videoContext?.selectedVideo;
  const titles = videoContext?.titles || [];
  const setTitles = videoContext?.setTitles || (() => {});
  const suggestedTitles: string[] = videoContext?.suggestedTitles || [];
  const suggestedMoods: string[] = videoContext?.suggestedMoods || [];
  const getSuggestedTitles: (mood?: string) => Promise<any> =
    videoContext?.getSuggestedTitles || (() => Promise.resolve());
  const suggestionsLoading: boolean = videoContext?.suggestionsLoading || false;

  const [title, setTitle] = useState("");
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [selectedMood, setSelectedMood] = useState<string | null>(null);

  function handleTitleChange(t: string) {
    setTitles((prev: string[]) => [...prev, t]);
    setTitle("");
  }

  function handleGetSuggestions() {
    setSelectedMood(null);
    getSuggestedTitles();
  }

  function handleMoodClick(mood: string) {
    const next = selectedMood === mood ? null : mood;
    setSelectedMood(next);
    getSuggestedTitles(next ?? undefined);
  }

  function handleToggleSuggestion(suggestion: string) {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(suggestion)) {
        next.delete(suggestion);
        setTitles((prevTitles: string[]) => prevTitles.filter((t) => t !== suggestion));
      } else {
        next.add(suggestion);
        setTitles((prevTitles: string[]) => [...prevTitles, suggestion]);
      }
      return next;
    });
  }

  return (
    <div className="w-full py-4 flex flex-col h-full">
      <AddTestStepper activeStep={activeStep} />
      <div className="flex flex-col md:flex-row py-4">
        <div className="px-6 py-4 flex-grow">
          <div className="pb-6">
            <h3 className="text-xl font-semibold pb-3">Thumbnails</h3>
            <DragDropFileUpload />
          </div>

          <div>
            <h3 className="text-xl font-semibold pb-3">Titles</h3>
            <div className="flex flex-col gap-1 mb-2">
              {titles.map((t: string, ind: number) => (
                <div
                  key={ind}
                  className="flex items-center justify-between px-3 py-2 rounded-md bg-muted"
                >
                  <span className="text-sm">{t}</span>
                  <button
                    onClick={() => setTitles(titles.filter((_: string, i: number) => i !== ind))}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <input
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTitleChange(title);
              }}
              placeholder="Add a title to test (press Enter)"
            />
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between pb-3">
              <h4 className="text-base font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                AI Title Suggestions
              </h4>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGetSuggestions}
                disabled={suggestionsLoading || !selectedVideo}
              >
                {suggestionsLoading ? "Generating..." : "Get Suggestions"}
              </Button>
            </div>

            {suggestedMoods.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-2">Make it more...</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedMoods.map((mood: string) => (
                    <button
                      key={mood}
                      onClick={() => handleMoodClick(mood)}
                      disabled={suggestionsLoading}
                      className={cn(
                        "px-3 py-1 text-xs rounded-full border transition-all capitalize disabled:opacity-50",
                        selectedMood === mood
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-foreground border-border hover:bg-muted"
                      )}
                    >
                      {mood}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {suggestedTitles.length > 0 ? (
              <div className="flex flex-col gap-2">
                {suggestedTitles.map((suggestion: string, ind: number) => {
                  const isSelected = selectedSuggestions.has(suggestion);
                  return (
                    <label
                      key={ind}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors",
                        isSelected
                          ? "bg-primary/10 border-primary/40"
                          : "bg-background border-border hover:bg-muted"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSuggestion(suggestion)}
                        className="rounded accent-primary"
                      />
                      <span className="flex-1 text-sm">{suggestion}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              !suggestionsLoading && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Click "Get Suggestions" to generate AI-powered title ideas
                </p>
              )
            )}
          </div>
        </div>

        <div className="ml-auto p-6">
          <VideoCard
            handlePageChangeUp={fn.handlePageChangeUp}
            views={selectedVideo?.views || "0"}
            uploaded={selectedVideo?.uploaded || new Date()}
            video_id={selectedVideo?.video_id || ""}
            title={selectedVideo?.title || ""}
            imgLink={selectedVideo?.imgLink || ""}
          />
        </div>
      </div>
      <AddTestPageNavButtons
        step={activeStep}
        handlePageChangeUp={fn.handlePageChangeUp}
        handlePageChangeDown={fn.handlePageChangeDown}
      />
    </div>
  );
}
